import { Button } from '@avanta/shared/components/ui/Button';
import { Select } from '@avanta/shared/components/ui/Select';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { ChevronRight } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AppointmentsReservePayload,
  type StepProps,
  type AppointmentsReserveFormData,
} from 'pages/AppointmentsReserve/types';
import { areRecordFieldsComplete } from 'pages/AppointmentsReserve/utils';
import { useGetOptions } from '../../../hooks/useGetOptions';
import { RecordFields } from '../../ui/RecordFields';
import { StepSection } from '../../ui/StepSection';
import { DataStepSkeleton } from './DataStepSkeleton';

export const DataStep = ({ handleNext }: StepProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const {
    setValue,
    watch,
    control,
    formState: { defaultValues },
  } = useFormContext<AppointmentsReserveFormData>();
  const [contractId, contractRateId, recordFieldValues] = watch([
    AppointmentsReservePayload.contractId,
    AppointmentsReservePayload.contractRateId,
    AppointmentsReservePayload.recordFields,
  ]);
  const {
    contractId: contractIdOptions,
    contractRateId: contractRateIdOptions,
    recordFields,
    isLoading,
  } = useGetOptions();

  const handleContractIdChange = (value: string): void => {
    setValue(AppointmentsReservePayload.contractRateId, '');
    setValue(AppointmentsReservePayload.recordFields, {});
    setValue(AppointmentsReservePayload.contractId, value);
  };

  const isValid =
    Boolean(contractId) &&
    Boolean(contractRateId) &&
    areRecordFieldsComplete(recordFields, recordFieldValues ?? {});

  if (isLoading) {
    return <DataStepSkeleton />;
  }

  return (
    <div className="flex flex-col gap-10 w-3xl">
      <StepSection
        title={t('AppointmentsReserve.Steps.data.administrative.title')}
        className="grid grid-cols-2 gap-6"
        containerClassName="w-full"
      >
        <Select
          label={t('AppointmentsReserve.Steps.data.administrative.contract.label')}
          options={contractIdOptions ?? []}
          placeholder={t('AppointmentsReserve.Steps.data.administrative.contract.placeholder')}
          required
          value={contractId}
          readOnly={Boolean(defaultValues?.contractId)}
          onValueChange={handleContractIdChange}
        />
        <Select
          label={t('AppointmentsReserve.Steps.data.administrative.rate.label')}
          options={contractRateIdOptions ?? []}
          placeholder={t('AppointmentsReserve.Steps.data.administrative.rate.placeholder')}
          required
          value={contractRateId}
          disabled={!contractId}
          readOnly={Boolean(defaultValues?.contractRateId)}
          onValueChange={value => setValue(AppointmentsReservePayload.contractRateId, value)}
        />
        {contractId ? (
          <RecordFields
            control={control}
            recordFields={recordFields}
            readOnlyInitialValues={defaultValues?.recordFields}
          />
        ) : null}
      </StepSection>
      <div className="flex w-full justify-end">
        <Button
          type="button"
          size="lg"
          icon="trailing"
          className="min-w-37 w-fit"
          disabled={!isValid}
          onClick={() => handleNext?.()}
        >
          {t('AppointmentsReserve.next')}
          <ChevronRight className="size-6" />
        </Button>
      </div>
    </div>
  );
};
